/**
 * SQLite Local Database using sql.js (WebAssembly)
 * 
 * This module provides a full SQL database in the browser that mirrors
 * the Supabase schema for offline-first PDV operations.
 */

import initSqlJs, { Database, SqlJsStatic } from 'sql.js';

// IndexedDB store for persisting SQLite database
const SQLITE_DB_STORE = 'sqlite_database';
const SQLITE_DB_KEY = 'actualfood_db';

let SQL: SqlJsStatic | null = null;
let db: Database | null = null;
let isInitialized = false;
let initPromise: Promise<Database> | null = null;

// ============ Schema Definition ============

const SCHEMA = `
-- Dados estáticos (sincronizados do servidor)
CREATE TABLE IF NOT EXISTS products (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  name TEXT NOT NULL,
  price REAL NOT NULL,
  category_id TEXT,
  image_url TEXT,
  description TEXT,
  is_active INTEGER DEFAULT 1,
  tracks_stock INTEGER DEFAULT 0,
  current_stock REAL,
  minimum_stock REAL,
  cost_per_unit REAL,
  barcode TEXT,
  production_location_id TEXT,
  available_pdv INTEGER DEFAULT 1,
  available_table INTEGER DEFAULT 1,
  available_tablet INTEGER DEFAULT 1,
  available_delivery INTEGER DEFAULT 1,
  is_featured INTEGER DEFAULT 0,
  sort_order INTEGER DEFAULT 0,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_products_restaurant ON products(restaurant_id);
CREATE INDEX IF NOT EXISTS idx_products_category ON products(category_id);
CREATE INDEX IF NOT EXISTS idx_products_barcode ON products(barcode);

CREATE TABLE IF NOT EXISTS categories (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  name TEXT NOT NULL,
  description TEXT,
  color TEXT,
  icon TEXT,
  sort_order INTEGER DEFAULT 0,
  is_active INTEGER DEFAULT 1,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_categories_restaurant ON categories(restaurant_id);

CREATE TABLE IF NOT EXISTS tables (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  number TEXT NOT NULL,
  name TEXT,
  status TEXT DEFAULT 'available',
  current_order_id TEXT,
  grouped_to_order_id TEXT,
  service_tax_enabled INTEGER DEFAULT 1,
  capacity INTEGER,
  position_x REAL,
  position_y REAL,
  is_active INTEGER DEFAULT 1,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_tables_restaurant ON tables(restaurant_id);

CREATE TABLE IF NOT EXISTS commands (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  number TEXT NOT NULL,
  status TEXT DEFAULT 'available',
  current_order_id TEXT,
  grouped_to_order_id TEXT,
  customer_name TEXT,
  service_tax_enabled INTEGER DEFAULT 1,
  is_active INTEGER DEFAULT 1,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_commands_restaurant ON commands(restaurant_id);

CREATE TABLE IF NOT EXISTS payment_methods (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  name TEXT NOT NULL,
  type TEXT,
  fee_percentage REAL DEFAULT 0,
  is_active INTEGER DEFAULT 1,
  accepts_change INTEGER DEFAULT 0,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_payment_methods_restaurant ON payment_methods(restaurant_id);

CREATE TABLE IF NOT EXISTS production_locations (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  name TEXT NOT NULL,
  printer_name TEXT,
  is_active INTEGER DEFAULT 1,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_production_locations_restaurant ON production_locations(restaurant_id);

CREATE TABLE IF NOT EXISTS combos (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  name TEXT NOT NULL,
  description TEXT,
  price REAL NOT NULL,
  image_url TEXT,
  combo_type TEXT,
  category_id TEXT,
  production_location_id TEXT,
  is_active INTEGER DEFAULT 1,
  available_pdv INTEGER DEFAULT 1,
  available_table INTEGER DEFAULT 1,
  available_tablet INTEGER DEFAULT 1,
  available_delivery INTEGER DEFAULT 1,
  requires_notes INTEGER DEFAULT 0,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_combos_restaurant ON combos(restaurant_id);

-- Dados dinâmicos (criados localmente, sincronizados para servidor)
CREATE TABLE IF NOT EXISTS orders (
  id TEXT PRIMARY KEY,
  restaurant_id TEXT NOT NULL,
  order_number INTEGER,
  order_type TEXT,
  table_number TEXT,
  tab_number TEXT,
  table_id TEXT,
  command_id TEXT,
  customer_name TEXT,
  subtotal REAL DEFAULT 0,
  discount REAL DEFAULT 0,
  discount_type TEXT,
  additional_fees REAL DEFAULT 0,
  service_tax REAL DEFAULT 0,
  couvert_quantity INTEGER DEFAULT 0,
  couvert_amount REAL DEFAULT 0,
  total REAL DEFAULT 0,
  status TEXT DEFAULT 'pending',
  people_count INTEGER,
  period_id TEXT,
  cash_register_id TEXT,
  waiter_name TEXT,
  waiter_session_id TEXT,
  notes TEXT,
  created_at TEXT NOT NULL,
  updated_at TEXT,
  closed_at TEXT,
  payment_method_id TEXT,
  payment_fee REAL DEFAULT 0,
  payment_label TEXT,
  customer_phone TEXT,
  delivery_address TEXT,
  delivery_fee REAL DEFAULT 0,
  is_public_order INTEGER DEFAULT 0,
  active_rodizio_combo_id TEXT,
  synced INTEGER DEFAULT 0,
  server_id TEXT
);

CREATE INDEX IF NOT EXISTS idx_orders_restaurant ON orders(restaurant_id);
CREATE INDEX IF NOT EXISTS idx_orders_status ON orders(status);
CREATE INDEX IF NOT EXISTS idx_orders_table ON orders(table_number);
CREATE INDEX IF NOT EXISTS idx_orders_synced ON orders(synced);

CREATE TABLE IF NOT EXISTS order_items (
  id TEXT PRIMARY KEY,
  order_id TEXT NOT NULL,
  product_id TEXT,
  combo_id TEXT,
  product_name TEXT NOT NULL,
  quantity INTEGER NOT NULL,
  unit_price REAL NOT NULL,
  total_price REAL NOT NULL,
  notes TEXT,
  status TEXT DEFAULT 'pending',
  combo_selections TEXT,
  production_location_id TEXT,
  sent_to_kitchen INTEGER DEFAULT 0,
  sent_at TEXT,
  printed_at TEXT,
  created_at TEXT NOT NULL,
  synced INTEGER DEFAULT 0,
  server_id TEXT,
  FOREIGN KEY (order_id) REFERENCES orders(id) ON DELETE CASCADE
);

CREATE INDEX IF NOT EXISTS idx_order_items_order ON order_items(order_id);
CREATE INDEX IF NOT EXISTS idx_order_items_synced ON order_items(synced);

CREATE TABLE IF NOT EXISTS order_payments (
  id TEXT PRIMARY KEY,
  order_id TEXT NOT NULL,
  payment_method_id TEXT NOT NULL,
  payment_method_name TEXT NOT NULL,
  amount REAL NOT NULL,
  fee_amount REAL DEFAULT 0,
  created_at TEXT NOT NULL,
  synced INTEGER DEFAULT 0,
  server_id TEXT,
  FOREIGN KEY (order_id) REFERENCES orders(id) ON DELETE CASCADE
);

CREATE INDEX IF NOT EXISTS idx_order_payments_order ON order_payments(order_id);

-- Fila de operações pendentes
CREATE TABLE IF NOT EXISTS sync_queue (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  operation TEXT NOT NULL,
  table_name TEXT NOT NULL,
  record_id TEXT NOT NULL,
  payload TEXT,
  priority INTEGER DEFAULT 0,
  created_at TEXT NOT NULL,
  status TEXT DEFAULT 'pending',
  attempts INTEGER DEFAULT 0,
  max_attempts INTEGER DEFAULT 5,
  last_error TEXT,
  last_attempt_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_sync_queue_status ON sync_queue(status);
CREATE INDEX IF NOT EXISTS idx_sync_queue_priority ON sync_queue(priority DESC, created_at ASC);

-- Metadados de sincronização
CREATE TABLE IF NOT EXISTS sync_metadata (
  key TEXT PRIMARY KEY,
  value TEXT,
  updated_at TEXT
);

-- Cache de consumos ativos (para visualização offline)
CREATE TABLE IF NOT EXISTS active_consumptions (
  id TEXT PRIMARY KEY,
  type TEXT NOT NULL,
  restaurant_id TEXT NOT NULL,
  order_id TEXT,
  order_number INTEGER,
  number TEXT NOT NULL,
  customer_name TEXT,
  items_json TEXT,
  subtotal REAL DEFAULT 0,
  total REAL DEFAULT 0,
  service_tax_enabled INTEGER DEFAULT 1,
  updated_at TEXT,
  synced_at TEXT
);

CREATE INDEX IF NOT EXISTS idx_active_consumptions_restaurant ON active_consumptions(restaurant_id);
`;

// ============ Initialization ============

/**
 * Initialize sql.js and the database
 */
export async function initSQLite(restaurantId?: string): Promise<Database> {
  if (db && isInitialized) {
    return db;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      console.log('[SQLite] Initializing sql.js...');
      
      // Load sql.js from CDN
      SQL = await initSqlJs({
        locateFile: () => `/sql-wasm.wasm`
      });

      // Try to restore from IndexedDB
      const savedData = await loadDatabaseFromIndexedDB();
      
      if (savedData) {
        console.log('[SQLite] Restoring database from IndexedDB...');
        db = new SQL.Database(savedData);
      } else {
        console.log('[SQLite] Creating new database...');
        db = new SQL.Database();
      }

      // Ensure schema exists
      db.run(SCHEMA);
      
      // Run migrations for existing databases (add new columns safely)
      try {
        db.run(`ALTER TABLE tables ADD COLUMN grouped_to_order_id TEXT`);
      } catch (_) { /* column already exists */ }
      try {
        db.run(`ALTER TABLE commands ADD COLUMN grouped_to_order_id TEXT`);
      } catch (_) { /* column already exists */ }

      // Idempotent column additions for existing databases
      const ensureColumn = (table: string, col: string, type: string) => {
        try {
          const info = db!.exec(`PRAGMA table_info(${table})`)[0];
          const cols = info?.values.map((r) => String(r[1])) ?? [];
          if (!cols.includes(col)) {
            db!.run(`ALTER TABLE ${table} ADD COLUMN ${col} ${type}`);
          }
        } catch (_) { /* noop */ }
      };

      ensureColumn('orders', 'payment_method_id', 'TEXT');
      ensureColumn('orders', 'payment_fee', 'REAL DEFAULT 0');
      ensureColumn('orders', 'payment_label', 'TEXT');
      ensureColumn('orders', 'customer_phone', 'TEXT');
      ensureColumn('orders', 'delivery_address', 'TEXT');
      ensureColumn('orders', 'delivery_fee', 'REAL DEFAULT 0');
      ensureColumn('orders', 'is_public_order', 'INTEGER DEFAULT 0');
      ensureColumn('orders', 'active_rodizio_combo_id', 'TEXT');
      ensureColumn('order_items', 'printed_at', 'TEXT');

      isInitialized = true;
      console.log('[SQLite] Database initialized successfully');

      // Set up auto-persist
      setupAutoPersist();

      return db;
    } catch (error) {
      console.error('[SQLite] Failed to initialize:', error);
      initPromise = null;
      throw error;
    }
  })();

  return initPromise;
}

/**
 * Get the database instance (must call initSQLite first)
 */
export function getDatabase(): Database | null {
  return db;
}

/**
 * Check if database is initialized
 */
export function isDatabaseReady(): boolean {
  return isInitialized && db !== null;
}

// ============ Query Functions ============

/**
 * Execute a SQL query that returns results (SELECT)
 */
export function query<T = Record<string, unknown>>(sql: string, params: unknown[] = []): T[] {
  if (!db) {
    throw new Error('Database not initialized. Call initSQLite() first.');
  }

  try {
    const stmt = db.prepare(sql);
    stmt.bind(params);

    const results: T[] = [];
    while (stmt.step()) {
      results.push(stmt.getAsObject() as T);
    }
    stmt.free();

    return results;
  } catch (error) {
    console.error('[SQLite] Query error:', sql, params, error);
    throw error;
  }
}

/**
 * Execute a SQL query that doesn't return results (INSERT, UPDATE, DELETE)
 */
export function execute(sql: string, params: unknown[] = []): void {
  if (!db) {
    throw new Error('Database not initialized. Call initSQLite() first.');
  }

  try {
    db.run(sql, params);
    // Mark for persistence
    schedulePersist();
  } catch (error) {
    console.error('[SQLite] Execute error:', sql, params, error);
    throw error;
  }
}

/**
 * Execute multiple statements in a transaction
 */
export function transaction(statements: Array<{ sql: string; params?: unknown[] }>): void {
  if (!db) {
    throw new Error('Database not initialized. Call initSQLite() first.');
  }

  try {
    db.run('BEGIN TRANSACTION');
    
    for (const { sql, params = [] } of statements) {
      db.run(sql, params);
    }
    
    db.run('COMMIT');
    schedulePersist();
  } catch (error) {
    db.run('ROLLBACK');
    console.error('[SQLite] Transaction error:', error);
    throw error;
  }
}

/**
 * Get the last inserted row ID
 */
export function lastInsertRowId(): number {
  if (!db) return 0;
  const result = db.exec('SELECT last_insert_rowid() as id');
  return result[0]?.values[0]?.[0] as number || 0;
}

// ============ Sync Queue Functions ============

/**
 * Add an operation to the sync queue
 */
export function addToSyncQueue(
  operation: 'INSERT' | 'UPDATE' | 'DELETE',
  tableName: string,
  recordId: string,
  payload?: Record<string, unknown>,
  priority: number = 0
): void {
  const sql = `
    INSERT INTO sync_queue (operation, table_name, record_id, payload, priority, created_at, status)
    VALUES (?, ?, ?, ?, ?, ?, 'pending')
  `;
  execute(sql, [
    operation,
    tableName,
    recordId,
    payload ? JSON.stringify(payload) : null,
    priority,
    new Date().toISOString()
  ]);
}

/**
 * Get pending sync operations
 */
export function getPendingSyncOperations(limit: number = 50): Array<{
  id: number;
  operation: string;
  table_name: string;
  record_id: string;
  payload: string | null;
  priority: number;
  created_at: string;
  attempts: number;
}> {
  return query(`
    SELECT id, operation, table_name, record_id, payload, priority, created_at, attempts
    FROM sync_queue
    WHERE status IN ('pending', 'failed') AND attempts < max_attempts
    ORDER BY priority DESC, created_at ASC
    LIMIT ?
  `, [limit]);
}

/**
 * Update sync queue item status
 */
export function updateSyncQueueStatus(
  id: number,
  status: 'pending' | 'syncing' | 'done' | 'failed',
  error?: string
): void {
  // Only increment attempts on failure or done, not on 'syncing' transition
  const shouldIncrementAttempts = status === 'failed' || status === 'done';
  execute(`
    UPDATE sync_queue
    SET status = ?, attempts = attempts + ${shouldIncrementAttempts ? 1 : 0}, last_error = ?, last_attempt_at = ?
    WHERE id = ?
  `, [status, error || null, new Date().toISOString(), id]);
}

/**
 * Remove completed sync operations
 */
export function clearCompletedSyncOperations(): void {
  execute(`DELETE FROM sync_queue WHERE status = 'done'`);
}

/**
 * Get sync queue statistics
 */
export function getSyncQueueStats(): {
  pending: number;
  syncing: number;
  failed: number;
  total: number;
} {
  const result = query<{ status: string; count: number }>(`
    SELECT status, COUNT(*) as count FROM sync_queue GROUP BY status
  `);
  
  const stats = { pending: 0, syncing: 0, failed: 0, total: 0 };
  for (const row of result) {
    if (row.status === 'pending') stats.pending = row.count;
    else if (row.status === 'syncing') stats.syncing = row.count;
    else if (row.status === 'failed') stats.failed = row.count;
    stats.total += row.count;
  }
  return stats;
}

// ============ Sync Metadata Functions ============

/**
 * Get sync metadata value
 */
export function getSyncMeta(key: string): string | null {
  const result = query<{ value: string }>(`SELECT value FROM sync_metadata WHERE key = ?`, [key]);
  return result[0]?.value || null;
}

/**
 * Set sync metadata value
 */
export function setSyncMeta(key: string, value: string): void {
  execute(`
    INSERT OR REPLACE INTO sync_metadata (key, value, updated_at)
    VALUES (?, ?, ?)
  `, [key, value, new Date().toISOString()]);
}

// ============ Persistence to IndexedDB ============

let persistTimeout: ReturnType<typeof setTimeout> | null = null;
const PERSIST_DELAY = 1000; // 1 second debounce

function schedulePersist(): void {
  if (persistTimeout) {
    clearTimeout(persistTimeout);
  }
  persistTimeout = setTimeout(() => {
    persistToIndexedDB().catch(console.error);
  }, PERSIST_DELAY);
}

function setupAutoPersist(): void {
  // Persist on page unload
  window.addEventListener('beforeunload', () => {
    if (db) {
      persistToIndexedDBSync();
    }
  });

  // Persist every 30 seconds as backup
  setInterval(() => {
    if (db && isInitialized) {
      persistToIndexedDB().catch(console.error);
    }
  }, 30000);
}

/**
 * Persist database to IndexedDB
 */
export async function persistToIndexedDB(): Promise<void> {
  if (!db) return;

  try {
    const data = db.export();
    const uint8Array = new Uint8Array(data);
    
    await saveToIndexedDB(uint8Array);
    console.log('[SQLite] Database persisted to IndexedDB');
  } catch (error) {
    console.error('[SQLite] Failed to persist database:', error);
  }
}

/**
 * Synchronous persist for beforeunload
 */
function persistToIndexedDBSync(): void {
  if (!db) return;
  
  try {
    const data = db.export();
    // Use localStorage as fallback for sync save
    const base64 = arrayBufferToBase64(data);
    localStorage.setItem('sqlite_backup', base64);
  } catch (error) {
    console.error('[SQLite] Sync persist failed:', error);
  }
}

async function saveToIndexedDB(data: Uint8Array): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('actualfood-sqlite', 1);
    
    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      if (!database.objectStoreNames.contains(SQLITE_DB_STORE)) {
        database.createObjectStore(SQLITE_DB_STORE);
      }
    };
    
    request.onsuccess = () => {
      const database = request.result;
      const transaction = database.transaction(SQLITE_DB_STORE, 'readwrite');
      const store = transaction.objectStore(SQLITE_DB_STORE);
      
      const putRequest = store.put(data, SQLITE_DB_KEY);
      putRequest.onerror = () => reject(putRequest.error);
      putRequest.onsuccess = () => {
        database.close();
        resolve();
      };
    };
  });
}

async function loadDatabaseFromIndexedDB(): Promise<Uint8Array | null> {
  // First try IndexedDB
  try {
    const data = await loadFromIndexedDB();
    if (data) return data;
  } catch (error) {
    console.warn('[SQLite] Failed to load from IndexedDB:', error);
  }

  // Fallback to localStorage
  try {
    const base64 = localStorage.getItem('sqlite_backup');
    if (base64) {
      localStorage.removeItem('sqlite_backup');
      return base64ToArrayBuffer(base64);
    }
  } catch (error) {
    console.warn('[SQLite] Failed to load from localStorage:', error);
  }

  return null;
}

async function loadFromIndexedDB(): Promise<Uint8Array | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('actualfood-sqlite', 1);
    
    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      if (!database.objectStoreNames.contains(SQLITE_DB_STORE)) {
        database.createObjectStore(SQLITE_DB_STORE);
      }
    };
    
    request.onsuccess = () => {
      const database = request.result;
      const transaction = database.transaction(SQLITE_DB_STORE, 'readonly');
      const store = transaction.objectStore(SQLITE_DB_STORE);
      
      const getRequest = store.get(SQLITE_DB_KEY);
      getRequest.onerror = () => {
        database.close();
        reject(getRequest.error);
      };
      getRequest.onsuccess = () => {
        database.close();
        resolve(getRequest.result || null);
      };
    };
  });
}

// ============ Utility Functions ============

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Export database as downloadable file
 */
export function exportDatabase(): Blob | null {
  if (!db) return null;
  const data = db.export();
  return new Blob([data], { type: 'application/x-sqlite3' });
}

/**
 * Clear all local data and reset database
 */
export async function resetDatabase(): Promise<void> {
  if (db) {
    db.close();
    db = null;
  }
  isInitialized = false;
  initPromise = null;

  // Clear from IndexedDB
  try {
    const request = indexedDB.deleteDatabase('actualfood-sqlite');
    await new Promise<void>((resolve) => {
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  } catch (error) {
    console.error('[SQLite] Failed to delete database:', error);
  }

  // Clear localStorage backup
  localStorage.removeItem('sqlite_backup');
}

/**
 * Get database size in bytes
 */
export function getDatabaseSize(): number {
  if (!db) return 0;
  const data = db.export();
  return data.byteLength;
}
